import { Link } from "react-router-dom";

import { relativeTime, useVesselPath } from "@/lib/hooks";
import type { RetestPlan } from "@/lib/provenance";

/**
 * The step that has to happen before any of the others: test the water again.
 *
 * This card is the visible half of the bug it was built for. The owner tested
 * FC 10.0, added a gallon of liquid chlorine, and "Do this, in order" went on
 * offering more liquid chlorine off the stale 10.0. Withholding that suggestion
 * server-side fixes the danger and leaves an empty list, which on this screen
 * reads as "nothing to do" — so the withheld dose has to be *replaced* by
 * something, not merely removed.
 *
 * The reason is shown as a cause and a projection rather than the server's full
 * sentence: one trip to the pool can supersede five parameters, and five
 * near-identical paragraphs is a wall of text at a railing. The sentences are
 * still on the rows below, written once, by the server.
 *
 * ## It names what to re-test, and does not claim to block anything
 *
 * The heading used to read "Re-test before adding anything" — a blanket claim
 * the screen contradicted two lines further down. Muriatic acid supersedes pH
 * and TA and leaves free chlorine untouched, so the app would correctly go on
 * offering a chlorine dose underneath a card telling the owner to add nothing.
 * With free chlorine below its floor that is not just confusing, it argues
 * against the one thing that actually needed doing.
 *
 * The card only ever covers *superseded* parameters, so naming them is both
 * shorter and true. And it can never block a listed dose: any suggestion still
 * on the list is by construction sized off figures nothing has touched, because
 * the ones sized off superseded figures were withheld server-side before the
 * screen saw them.
 */
export default function RetestCard({
  plan,
  interferencePpm,
  chlorine,
}: {
  plan: RetestPlan;
  /** `ph_interference_ppm` from the evaluation. */
  interferencePpm?: string;
  /** Free chlorine as it stands, for deciding whether to mention R-0007. */
  chlorine?: string | null;
}) {
  const vp = useVesselPath();
  const causes = plan.causes
    .map((action) => `${action.label} (${relativeTime(action.occurred_at)})`)
    .join(", ");

  // Labels are sentence-case ("Free chlorine", "Total alkalinity") and belong
  // mid-sentence here — but "pH" is not a capitalised word, it is a name, and
  // "re-test ph" looks like a typo. Lower the first letter only when the second
  // one is already lower, which leaves acronyms alone.
  const labels = plan.rows.map((row) =>
    /^[A-Z][a-z]/.test(row.label)
      ? row.label[0].toLowerCase() + row.label.slice(1)
      : row.label,
  );
  const named =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

  // Testing pH at this pool's chlorine levels needs the sample neutralised
  // first, and the moment the app sends someone to go and test is the moment
  // worth saying so. The app already refuses to dose pH from an un-neutralised
  // reading above this threshold — this is the same fact, said early enough to
  // save the trip rather than late enough to explain a wasted one.
  const ppm = interferencePpm === undefined ? null : Number(interferencePpm);
  const fc =
    chlorine === null || chlorine === undefined ? null : Number(chlorine);
  const neutralise =
    plan.rows.some((row) => row.parameter === "ph") &&
    ppm !== null &&
    fc !== null &&
    Number.isFinite(ppm) &&
    Number.isFinite(fc) &&
    fc >= ppm;

  return (
    <li className="card p-4">
      <p className="text-sm font-medium">Re-test {named}</p>
      <p className="mt-1 text-sm text-(--color-ink-muted)">
        {causes
          ? `Since the last test: ${causes}.`
          : "Something logged since the last test moved these."}
      </p>

      <ul className="mt-3 space-y-1.5">
        {plan.rows.map((row) => (
          <li
            key={row.parameter}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span className="min-w-0 truncate">{row.label}</span>
            <span className="shrink-0 text-(--color-ink-muted)">
              <span className="figure">{row.was ?? "—"}</span>
              {row.projected ? (
                <>
                  {" → about "}
                  <span className="figure">{row.projected}</span>
                </>
              ) : (
                // Never a made-up number. An event carries no prediction, and
                // nor does a dose logged by hand, so the honest answer to "where
                // did it land" is that nobody knows until it is tested.
                " → not predicted"
              )}
            </span>
          </li>
        ))}
      </ul>

      {plan.readyAt && (
        <p className="mt-3 text-sm">
          <RetestWhen readyAt={plan.readyAt} />
        </p>
      )}

      {neutralise && (
        <p className="mt-3 text-xs text-(--color-ink-faint)">
          Neutralise the pH sample with a drop of R-0007 first — free chlorine
          is over <span className="figure">{interferencePpm}</span> ppm, which
          phenol red cannot cope with, and tick the box on the reading.
        </p>
      )}

      <Link to={vp("/log")} className="btn btn-primary mt-3 w-full text-sm">
        Log a reading
      </Link>
    </li>
  );
}

/**
 * When the water is worth testing again — and that it is not a chore for now.
 *
 * The card named the parameters and left the timing to be guessed, which at a
 * railing with an empty jug in hand reads as "go and do it". A chlorine test two
 * minutes after the pour measures the plume: it returns a real number, that
 * number is wrong, and nothing on the reading afterwards says so.
 *
 * Once the wait has passed the line disappears rather than turning into a
 * prompt. Nothing here has ever asked for a test on a schedule — the card is a
 * guard against sizing the *next* dose off a projection, not a task — and a
 * standing "ready now" would quietly convert it into one.
 */
function RetestWhen({ readyAt }: { readyAt: string }) {
  const ready = new Date(readyAt);
  if (Number.isNaN(ready.getTime()) || ready.getTime() <= Date.now())
    return null;

  // A clock time beats a duration for the same reason the dose sequence quotes
  // sundown: the person reading it is holding something and should not have to
  // do arithmetic. Past a day out the time of day is noise, so say the day.
  const soon = ready.getTime() - Date.now() < 20 * 60 * 60 * 1000;
  const when = soon
    ? ready.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      })
    : ready.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      });

  return (
    <>
      Worth testing from about <span className="figure">{when}</span>, with the
      pump running. Before that you would be testing what you just added rather
      than the pool. These figures are projections in the meantime — fine to
      leave, just do not size another dose off them.
    </>
  );
}
